import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Award, TrendingUp, ArrowRight } from 'lucide-react';
import PageLayout, { PageHero } from '../components/PageLayout';
import { TeamArt } from '../components/illustrations';

const STATS = [
  { value: 'Beta',   label: 'Currently in beta', accent: 'spectrum-8' },
  { value: 'Free',   label: '1 month, no card',  accent: 'spectrum-6' },
  { value: '30 min', label: 'Setup time',        accent: 'spectrum-4' },
  { value: '0',      label: 'Hardware needed',   accent: 'spectrum-2' },
];

const VALUES = [
  { icon: Target,     accent: 'accent-1', title: 'Customer first', description: 'We put customers at the centre of every decision. Your success is our success.' },
  { icon: Users,      accent: 'accent-5', title: 'Innovation',     description: 'We build what the market needs next, not what everyone else already built.' },
  { icon: Award,      accent: 'accent-4', title: 'Excellence',     description: 'High standards in product quality, support, and every customer interaction.' },
  { icon: TrendingUp, accent: 'accent-3', title: 'Growth',         description: 'We grow with our customers — their expansion drives our roadmap.' },
];

const About = () => (
  <PageLayout
    title="About Us — Built for Real Outbound Teams"
    description="WASLX was built to replace expensive call center infrastructure with a smartphone. Learn our story, mission, and why mobile-first is the future of outbound calling."
    path="/about"
  >
    <PageHero
      eyebrow="About WASLX"
      title={
        <>
          Built for the teams <span className="display-accent">making the calls</span>
        </>
      }
      sub="WASLX was founded to solve a real problem: call center infrastructure costs too much, takes too long to set up, and forces agents to sit at a desk."
      art={TeamArt}
    />

    {/* A spectrum row: each tile blends into the next stop, so the four read
        as one ramp rather than four unrelated colours. */}
    <section className="section section--muted section--tight">
      <div className="container-page">
        <div className="stagger-children grid grid-cols-2 gap-5 md:grid-cols-4">
          {STATS.map(stat => (
            <div key={stat.label} className={`${stat.accent} stat-tile animate-fade-up text-center`}>
              <div className="stat-tile__value">{stat.value}</div>
              <div className="stat-tile__label">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section section--surface">
      <div className="container-page">
        <div className="mx-auto max-w-3xl">
          <span className="eyebrow accent-1">Our story</span>
          <h2 className="section-title !text-3xl">Why we built WASLX</h2>
          <div className="mt-6 space-y-4 leading-relaxed text-content-muted">
            <p>
              WASLX started when we noticed that every Indian BPO and sales team was paying for
              hardware they did not need, spending weeks setting up infrastructure, and watching
              agents sit idle waiting for manual dials to connect.
            </p>
            <p>
              We built it for the Android phone already in every agent’s pocket. No SIM boxes, no
              desk phones, no expensive PBX. Just an app, a SIM card, and a dashboard that shows you
              everything in real time.
            </p>
            <p>
              We are freshly launched and in beta, looking for the first teams willing to build with
              us. Your feedback shapes the product and your use case becomes our roadmap. In return
              you get a full month completely free.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="section section--muted">
      <div className="container-page">
        <div className="section-head">
          <h2 className="section-title">What we stand for</h2>
        </div>

        <div className="stagger-children mx-auto grid max-w-4xl gap-5 sm:grid-cols-2">
          {VALUES.map(value => {
            const Icon = value.icon;
            return (
              <div
                key={value.title}
                className={`${value.accent} card card--hover animate-fade-up flex items-start gap-4`}
              >
                <span className="icon-tile">
                  <Icon className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="card-title">{value.title}</h3>
                  <p className="card-body">{value.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    <section className="section section--surface">
      <div className="container-page">
        <div className="card-art card-art--brand card-art--sheen has-orbs mx-auto max-w-2xl text-center">
          <span className="shape shape--spark shape--sm shape--tr" aria-hidden="true" />
          <h2 className="text-section font-bold leading-tight">Join the beta</h2>
          <p className="mx-auto mt-4 max-w-md text-lede leading-relaxed opacity-90">
            We are looking for early teams to build with. Full access, free for your first month —
            no card and no commitment.
          </p>
          <Link to="/contact" className="btn btn-on-art btn--lg mt-8">
            Claim free beta access
            <ArrowRight className="h-[18px] w-[18px]" />
          </Link>
        </div>
      </div>
    </section>
  </PageLayout>
);

export default About;
